import {
  all, call, put, take,
} from 'redux-saga/effects';
import {
  chunk, compose, flatMap, get, includes, map, orderBy, reject,
} from 'lodash/fp';

import * as errorActions from '../../errors/actions';
import { FIREBASE_REF } from '../../constants';
import firebase from '../../utils/firebase';
import * as contactServices from '../contact/services';
import * as actions from './actions';

export function* fetchNewsletters() {
  while (true) {
    try {
      yield take(actions.FETCH_NEWSLETTERS_REQUEST);

      const { data: guardianContacts } = yield call(contactServices.fetchGuardianContactsByUser);

      const ref = yield call(firebase.firestore.collection, `${FIREBASE_REF}/school-newsletters`);

      const [snapshots, responses] = yield all([
        all(flatMap((guardianContact) => [
          call([ref.where(`targets.${guardianContact.id}`, '==', true), 'get']),
          call([ref.where(`targets.${guardianContact.id}`, '==', false), 'get']),
        ], guardianContacts)),
        all(map((guardianContact) => call(contactServices.fetchContact, { cid: guardianContact.contact.id }), guardianContacts)),
      ]);

      const children = map((response) => response.data, responses);

      const newsletters = compose(
        orderBy(['status', 'target.recorded', 'sendAt'], ['desc', 'asc', 'desc']),
        flatMap.convert({ cap: false })((chunkedNewsletters, index) => map((newsletter) => ({
          title: newsletter.title,
          school: newsletter.school,
          schoolName: newsletter.schoolName || '',
          id: newsletter.id,
          sendAt: newsletter.sendAt || Date.parse(newsletter.createdAt.toDate()),
          status: newsletter.status || 'closed',
          target: {
            id: get('id', children[index]),
            clazzNumber: get('number', children[index]),
            name: get('name', children[index]),
            recorded: newsletter.targets[get('id', guardianContacts[index])],
            clazzGroup: get('class_group', children[index]) && {
              grade: get('class_group.grade', children[index]),
              clazzName: get('class_group.class_name', children[index]),
            },
            customGroups: map((customGroup) => ({
              id: customGroup.id,
              name: customGroup.name,
              isUnspecifiedGroup: customGroup.is_unspecified_group,
            }), get('custom_groups', children[index])),
            gcid: get('primary_guardian_contact.id', children[index]),
          },
        }), chunkedNewsletters)),
        map(flatMap((snapshot) => map((doc) => ({ id: doc.id, ...doc.data() }), snapshot.docs))),
        chunk(2),
      )(snapshots);

      yield put(actions.fetchNewslettersSuccess({ newsletters }));
    } catch (error) {
      yield put(actions.fetchNewslettersFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* fetchFavorites() {
  while (true) {
    try {
      yield take(actions.FETCH_FAVORITES_REQUEST);

      const currentUser = yield call(firebase.auth.getCurrentUser);
      const favoriteRef = yield call(firebase.firestore.doc, `${FIREBASE_REF}/favorites/${currentUser.uid}`);

      const doc = yield call(firebase.firestore.getDocument, favoriteRef);

      const favorites = doc.exists ? doc.data() : { data: [] };

      yield put(actions.fetchFavoritesSuccess({ favorites: favorites.data }));
    } catch (error) {
      yield put(actions.fetchFavoritesFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}

export function* updateFavorites() {
  while (true) {
    try {
      const action = yield take(actions.UPDATE_FAVORITES_REQUEST);

      const { value } = action.payload;

      const currentUser = yield call(firebase.auth.getCurrentUser);

      const ref = yield call(firebase.firestore.doc, `${FIREBASE_REF}/favorites/${currentUser.uid}`);

      const updateFunction = async (transaction) => {
        const doc = await transaction.get(ref);

        const favorites = doc.exists ? doc.data() : { data: [] };

        transaction.set(ref, {
          data: includes(value, favorites.data)
            ? reject((favorite) => favorite === value, favorites.data) : [...favorites.data, value],
        });
      };

      yield call(firebase.firestore.runTransaction, updateFunction);

      yield put(actions.updateFavoritesSuccess({ value }));
    } catch (error) {
      yield put(actions.updateFavoritesFailure({ error }));

      yield put(errorActions.errorOccured({ error }));
    }
  }
}
